import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import cn from "classnames";
import styles from "./PartnerDetail.module.sass";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";

// Screens
import NameAndDescription from "./NameAndDescription";
import Member from "./Member";

// Components  
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";

// Services
import { getPartnerById, updatePartner } from "../../services/partner.service";
import { showSuccess, showError } from "../../services/toast.service";

// Fonction pour formater la date
const formatDate = (dateString) => {
  if (!dateString) return "Non définie";
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Fonction pour calculer les jours restants
const calculateDaysRemaining = (endDate) => {
  if (!endDate) return null;
  const end = new Date(endDate);
  const now = new Date();
  const diffTime = end - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

const navigation = ["Coordonnées", "Membres", "Facturation"];

const PartnerDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [partner, setPartner] = useState(location.state?.partner || null);
  const [loading, setLoading] = useState(!location.state?.partner);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Récupérer le partenaire depuis l'API si il n'est pas déjà disponible
  useEffect(() => {
    // Si nous avons déjà le partenaire depuis la navigation, ne pas le récupérer à nouveau
    if (location.state?.partner) return;
    
    const fetchPartner = async () => {
      setLoading(true);
      setError(null);
      try {
        const partnerData = await getPartnerById(id);
        if (partnerData) {
          setPartner(partnerData);
        } else {
          setError("Partenaire non trouvé");
          setTimeout(() => navigate("/partners"), 2000);
        }
      } catch (err) {
        console.error("Erreur lors de la récupération du partenaire:", err);
        setError("Impossible de charger les détails du partenaire");
        setTimeout(() => navigate("/partners"), 2000);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPartner();
    } else {
      setError("ID de partenaire non valide");
      setTimeout(() => navigate("/partners"), 2000);
    }
  }, [id, navigate, location.state]);

  // Fonction pour mettre à jour le partenaire
  const updatePartnerData = (updatedPartner) => {
    setPartner(updatedPartner);
  };

  // Gestion de l'upload du logo
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      showError('Veuillez sélectionner un fichier image');
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('Le fichier est trop volumineux (max 5MB)');
      return;
    }

    try {
      setIsUploading(true);
      
      // Créer un FormData pour l'upload
      const formData = new FormData();
      formData.append('logo', file);

      // Mettre à jour le partenaire avec le nouveau logo
      const updatedPartner = await updatePartner(partner.id, formData);
      setPartner(updatedPartner);
      showSuccess('Logo mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'upload du logo:', error);
      showError('Erreur lors de la mise à jour du logo');
    } finally {
      setIsUploading(false);
    }
  };

  // Gestion du drag & drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  // Obtenir l'URL du logo (même logique que AccountPartner/Profile)
  const getLogoUrl = () => {
    if (partner?.logo) {
      // Si c'est déjà une URL complète
      if (partner.logo.startsWith('http')) {
        return partner.logo;
      }
      // Sinon, construire l'URL PocketBase
      return `${process.env.REACT_APP_POCKETBASE_URL}/api/files/partners/${partner.id}/${partner.logo}`;
    }
    return '/images/content/company_logo.png'; // Fallback par défaut
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loading}>Chargement des détails du partenaire...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.error}>{error}</div>
        <div>Redirection vers la liste des partenaires...</div>
      </div>
    );
  }

  if (!partner) {
    return null;
  }

  // Calculer les jours restants pour les comptes démo
  const daysRemaining = partner.demo_account ? calculateDaysRemaining(partner.subscription_end_date) : null;

  return (
    <>
      <div className={styles.shop}>


        <Link className={cn("button-white", styles.button)} to="/partners">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        <Card className={styles.card}>

          {/* Profile */}

          <div className={styles.profile}>
            <div className={styles.details}>
              <div className={styles.avatar}>
                {partner.logo ? (
                  <>
                    <img src={getLogoUrl()} alt="Logo du partenaire" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      id="logo-upload"
                      disabled={isUploading}
                    />
                    <label htmlFor="logo-upload" className={styles.add}>
                      <Icon name="add" />
                    </label>
                  </>
                ) : (
                  <div 
                    className={cn(styles.dropZone, { [styles.dragActive]: dragActive })}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      id="logo-upload"
                      disabled={isUploading}
                    />
                    <label htmlFor="logo-upload" className={styles.dropLabel}>
                      {isUploading ? (
                        <Icon name="loader" size="24" />
                      ) : (
                        <Icon name="upload" size="24" />
                      )}
                    </label>
                  </div>
                )}
              </div>
              <div className={styles.wrap}>
                <div className={cn("h4", styles.man)}>{partner.name || 'Partenaire'}</div>
                <div className={styles.login}>
                  {partner.subscription_plan || 'Non spécifié'}
                </div>
              </div>
            </div>
            <div className={styles.contacts}>
              <div className={styles.statusInfo}>
                <div className={styles.statusGroup}>
                  <div className={styles.statusLabel}>Statut</div>
                  <div className={cn(
                    styles.statusBadge,
                    'status-type',
                    partner.is_active ? 'status-green-dark' : 'status-red-dark'
                  )}>
                    {partner.is_active ? 'Actif' : 'Inactif'}
                  </div>
                </div>
                {partner.demo_account && (
                  <div className={styles.statusGroup}>
                    <div className={styles.statusLabel}>Type</div>
                    <div className={cn(
                      styles.statusBadge,
                      'status-type',
                      'status-blue-dark'
                    )}>
                      Demo
                    </div>
                  </div>
                )}
                {partner.demo_account && daysRemaining !== null && (
                  <div className={styles.statusGroup}>
                    <div className={styles.statusLabel}>Temps restant</div>
                    <div className={cn(
                      styles.statusBadge,
                      'status-type',
                      daysRemaining > 7 ? 'status-green-dark' : 'status-red-dark'
                    )}>
                      {daysRemaining > 0 ? `${daysRemaining} jour${daysRemaining > 1 ? 's' : ''}` : 'Expiré'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className={styles.control}>
            <div className={cn(styles.nav, styles.desktopNav)}>
              {navigation.map((x, index) => (
                <button
                  className={cn(styles.link, {
                    [styles.active]: index === activeIndex,
                  })}
                  onClick={() => setActiveIndex(index)}
                  key={index}
                >
                  {x}
                </button>
              ))}
            </div>

            {/* Mobile Navigation */}
            <div className={styles.mobileNav}>
              <Dropdown
                className={styles.dropdown}
                value={navigation[activeIndex]}
                setValue={(value) => {
                  const index = navigation.findIndex(x => x === value);
                  if (index !== -1) setActiveIndex(index);
                }}
                options={navigation}
              />
            </div>
          </div>

        </Card>

        {/* Pages */}
        <div className={styles.wrap}>
          {activeIndex === 0 && (
            <>
              <NameAndDescription partner={partner} onPartnerUpdate={updatePartnerData} />
            </>
          )}
          {activeIndex === 1 && (
            <>
              <Member partner={partner}/>
            </>
          )}
        </div>

      </div>
    </>
  );
};

export default PartnerDetail;
